// Header.jsx - FULLY RESPONSIVE VERSION

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBars, FaBell, FaChevronDown, FaSearch, FaTimes,
  FaUser, FaCog, FaSignOutAlt, FaEnvelope, FaStar, FaHeart,
  FaExclamationTriangle, FaTimesCircle, FaCheckCircle,
  FaSpinner
} from 'react-icons/fa';
import {
  getUserDashboard, getNotifications,
  markNotificationRead,
  deleteNotification,
} from '../../Api/CoreApi';

const Header = ({ toggleSidebar, isMobile, sidebarOpen }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const modalRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    fetchHeaderData();
    fetchNotifications();

    const handleClickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target))
        setShowNotifications(false);
      if (profileRef.current && !profileRef.current.contains(e.target))
        setShowProfileMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);

    // ✅ ADDED: Listen for profile updates from anywhere in the app
    const handleProfileUpdate = () => {
      console.log("🔄 Profile updated event received, fetching fresh Header data...");
      fetchHeaderData();
    };
    window.addEventListener('profileUpdated', handleProfileUpdate);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // ✅ ADDED: Cleanup listener
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, []);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Lock body scroll when notification dropdown open on mobile
  useEffect(() => {
    if (isMobile && showNotifications) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, showNotifications]);

  const fetchNotifications = async () => {
    try {
      setNotificationLoading(true);
      const data = await getNotifications(20);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('❌ Error fetching notifications:', error);
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId = null) => {
    try {
      await markNotificationRead(notificationId);
      if (notificationId) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('❌ Error marking as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('❌ Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      profile_view: '👀',
      new_profile: '🆕',
      connection_request: '💖',
      request_accepted: '✅',
      request_rejected: '❌',
      new_plan: '📦',
      message: '💬',
      shortlist: '⭐',
    };
    return icons[type] || '🔔';
  };

  const getNotificationLink = (notification) => {
    if (notification.link) return notification.link;
    switch (notification.notification_type) {
      case 'profile_view':
      case 'new_profile':
        return `/user-profile/${notification.from_user_id}`;
      case 'connection_request':
        return '/requests';
      case 'request_accepted':
        return `/messages?chat=${notification.from_user_id}`;
      case 'shortlist':
        return `/user-profile/${notification.from_user_id}`;
      case 'new_plan':
        return '/recharge';
      default:
        return '#';
    }
  };

  const fetchHeaderData = async () => {
    try {
      setLoading(true);
      const data = await getUserDashboard();
      let user = null;
      if (data?.currentUser) user = data.currentUser;
      else if (data?.user) user = data.user;
      else if (data?.data?.user) user = data.data.user;
      else if (data?.username || data?.name) user = data;
      setUserData(user || {});
    } catch (error) {
      const username = localStorage.getItem('username') || 'User';
      setUserData({ name: username });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutClick = () => {
    setShowProfileMenu(false);
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    ['access_token','refresh_token','user_id','profile_id',
     'username','role','refer','user','user_data'].forEach(k => localStorage.removeItem(k));
    setShowLogoutModal(false);
    navigate('/User_Login');
  };

  const handleLogoutCancel = () => setShowLogoutModal(false);

  const getDisplayName = () => {
    if (userData)
      return userData.username || userData.firstname || userData.name || userData.fullname || 'User';
    return localStorage.getItem('username') || 'User';
  };

  const getAvatarUrl = () => {
    if (userData?.image) return userData.image;
    const name = getDisplayName();
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=ff0055&color=fff&size=40`;
  };

  const getProfileId = () => {
    if (userData) {
      const profileId = userData.profile_id || userData.profileId || userData.profile;
      if (profileId) return profileId;
    }
    return (
      localStorage.getItem('profile_id') ||
      userData?.user_id || userData?.id ||
      localStorage.getItem('user_id') || ''
    );
  };

  const getUserId = () => {
    if (userData) return userData.user_id || userData.id || localStorage.getItem('user_id') || '';
    return localStorage.getItem('user_id') || '';
  };

  const displayName = getDisplayName();
  const avatarUrl   = getAvatarUrl();
  const profileId   = getProfileId();
  const userId      = getUserId();
  const displayId   = profileId || userId;

  /* ─────────────────────────────────────────────
     STYLES
  ───────────────────────────────────────────── */
  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');

    /* ── Reset ──────────────────────────────────── */
    *, *::before, *::after { box-sizing: border-box; }

    /* ── Header shell ───────────────────────────── */
    .dh-header {
      background: linear-gradient(135deg,#9b003d 0%,#c4004f 30%,#e91e63 65%,#ff5c93 100%);
      padding: 0 16px;
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: sticky;
      top: 0;
      z-index: 100;
      box-shadow: 0 4px 18px rgba(255,0,85,.18);
      font-family: 'Poppins', sans-serif;
    }

    /* ── Left / Right clusters ──────────────────── */
    .dh-left  { display: flex; align-items: center; gap: 10px; }
    .dh-right { display: flex; align-items: center; gap: 6px;  }

    /* ── Icon buttons (hamburger, bell, search-toggle) */
    .dh-icon-btn {
      padding: 9px;
      background: rgba(255,255,255,.14);
      border: 1.5px solid rgba(255,255,255,.2);
      border-radius: 10px;
      cursor: pointer;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background .2s, transform .15s;
      flex-shrink: 0;
    }
    .dh-icon-btn:hover  { background: rgba(255,255,255,.25); }
    .dh-icon-btn:active { transform: scale(.94); }

    /* ── Desktop search bar ──────────────────────── */
    .dh-search-desktop {
      position: relative;
    }
    .dh-search-desktop .dh-search-icon-inner {
      position: absolute;
      left: 13px;
      top: 50%;
      transform: translateY(-50%);
      color: rgba(255,255,255,.7);
      pointer-events: none;
      font-size: 13px;
    }
    .dh-search-input {
      padding: 9px 14px 9px 38px;
      width: 280px;
      border: 1.5px solid rgba(255,255,255,.28);
      border-radius: 12px;
      font-size: 13px;
      outline: none;
      background: rgba(255,255,255,.14);
      backdrop-filter: blur(10px);
      color: #fff;
      font-family: 'Poppins', sans-serif;
      transition: border .25s, background .25s, box-shadow .25s;
    }
    .dh-search-input::placeholder { color: rgba(255,255,255,.65); }
    .dh-search-input:focus {
      border-color: rgba(255,255,255,.7);
      background: rgba(255,255,255,.2);
      box-shadow: 0 0 0 3px rgba(255,255,255,.12);
    }

    /* ── Notification bell ───────────────────────── */
    .dh-notif-wrap { position: relative; }
    .dh-notif-btn  { position: relative; }
    .dh-notif-pulse {
      position: absolute;
      top: 6px; right: 6px;
      width: 8px; height: 8px;
      background: #ffeb3b;
      border-radius: 50%;
      border: 2px solid #c4004f;
      animation: pulse 2s infinite;
    }
    .dh-notif-count {
      position: absolute;
      top: -5px; right: -5px;
      background: #f44336;
      color: #fff;
      font-size: 9px;
      font-weight: 800;
      padding: 2px 5px;
      border-radius: 999px;
      border: 2px solid #c4004f;
      min-width: 17px;
      text-align: center;
      line-height: 1.2;
      pointer-events: none;
    }
    @keyframes pulse {
      0%,100% { transform: scale(1);   opacity: 1; }
      50%      { transform: scale(1.4); opacity: .8; }
    }

    /* ── Profile button ──────────────────────────── */
    .dh-profile-wrap { position: relative; }
    .dh-profile-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 10px 4px 4px;
      background: rgba(255,255,255,.14);
      border: 1.5px solid rgba(255,255,255,.2);
      border-radius: 999px;
      cursor: pointer;
      transition: background .2s, transform .15s;
      backdrop-filter: blur(8px);
      flex-shrink: 0;
    }
    .dh-profile-btn:hover  { background: rgba(255,255,255,.24); }
    .dh-profile-btn:active { transform: scale(.97); }
    .dh-avatar {
      width: 34px; height: 34px;
      border-radius: 50%;
      border: 2px solid rgba(255,255,255,.55);
      box-shadow: 0 3px 10px rgba(0,0,0,.15);
      object-fit: cover;
      flex-shrink: 0;
    }
    .dh-profile-name {
      font-size: 13px;
      font-weight: 700;
      color: #fff;
      max-width: 110px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .dh-profile-id-pill {
      font-size: 10px;
      color: rgba(255,255,255,.78);
      background: rgba(255,255,255,.12);
      padding: 2px 8px;
      border-radius: 20px;
      border: 1px solid rgba(255,255,255,.18);
      font-weight: 500;
      white-space: nowrap;
    }
    .dh-chevron {
      font-size: 10px;
      color: rgba(255,255,255,.7);
    }

    /* ── Shared dropdown shell ───────────────────── */
    .dh-dropdown {
      position: absolute;
      right: 0;
      top: calc(100% + 10px);
      background: #fff;
      border-radius: 18px;
      box-shadow: 0 16px 48px rgba(155,0,61,.16);
      border: 1.5px solid #ffe4ec;
      z-index: 200;
      overflow: hidden;
      width: 390px;
    }
    .dh-dropdown-header {
      padding: 14px 18px;
      border-bottom: 1.5px solid #fff0f3;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: linear-gradient(135deg,#fffafc,#fff0f3);
      flex-shrink: 0;
    }
    .dh-dropdown-title {
      margin: 0;
      font-size: 14.5px;
      font-weight: 800;
      color: #1a0a10;
      display: flex;
      align-items: center;
      gap: 7px;
      flex-wrap: wrap;
    }
    .dh-new-badge {
      font-size: 10.5px;
      color: #ff0055;
      font-weight: 700;
      background: #fff0f3;
      padding: 2px 10px;
      border-radius: 999px;
    }
    .dh-mark-all {
      font-size: 11.5px;
      color: #ff0055;
      background: none;
      border: none;
      cursor: pointer;
      font-weight: 700;
      white-space: nowrap;
      padding: 0;
    }
    .dh-mark-all:hover    { color: #c2185b; }
    .dh-mark-all:disabled { opacity: .5; cursor: not-allowed; }

    /* ── Notification list ───────────────────────── */
    .dh-dropdown-body {
      max-height: 400px;
      overflow-y: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }
    .dh-dropdown-body::-webkit-scrollbar { width: 4px; }
    .dh-dropdown-body::-webkit-scrollbar-thumb {
      background: #ffc2d1;
      border-radius: 999px;
    }

    .dh-notif-item {
      padding: 12px 18px;
      border-bottom: 1px solid #fff0f3;
      cursor: pointer;
      display: flex;
      align-items: flex-start;
      gap: 12px;
      transition: background .18s;
      position: relative;
    }
    .dh-notif-item:last-child { border-bottom: none; }
    .dh-notif-item:hover  { background: #fffafc; }
    .dh-notif-item.unread { background: linear-gradient(90deg,#fff5f7,#fff); }
    .dh-notif-item.unread::before {
      content: '';
      position: absolute;
      left: 0; top: 0; bottom: 0;
      width: 4px;
      background: linear-gradient(180deg,#ff0055,#ff4d88);
      border-radius: 0 3px 3px 0;
    }
    .dh-notif-emoji {
      font-size: 19px;
      width: 38px; height: 38px;
      border-radius: 11px;
      background: linear-gradient(135deg,#fff0f3,#ffe4ec);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .dh-notif-content { flex: 1; min-width: 0; }
    .dh-notif-text {
      font-size: 13px;
      margin: 0;
      color: #444;
      font-weight: 500;
      line-height: 1.45;
      word-break: break-word;
    }
    .dh-notif-text strong {
      display: block;
      color: #1a0a10;
      font-weight: 700;
      margin-bottom: 2px;
    }
    .dh-notif-time {
      font-size: 11px;
      color: #bbb;
      margin: 5px 0 0;
      font-weight: 500;
    }
    .dh-notif-delete {
      background: none;
      border: none;
      color: #ddd;
      cursor: pointer;
      padding: 5px;
      margin: -5px -4px 0 0;
      transition: color .18s;
      flex-shrink: 0;
      border-radius: 6px;
    }
    .dh-notif-delete:hover { color: #f44336; background: #fef2f2; }

    /* ── Empty / Loading states ──────────────────── */
    .dh-empty-notif {
      padding: 44px 20px;
      text-align: center;
      color: #bbb;
      font-size: 14px;
    }
    .dh-empty-notif-icon {
      font-size: 46px;
      display: block;
      margin-bottom: 12px;
    }
    .dh-loading-notif {
      padding: 36px 20px;
      text-align: center;
      color: #ccc;
      font-size: 13px;
    }
    .dh-spinner { animation: spin 1s linear infinite; display: inline-block; }
    @keyframes spin { to { transform: rotate(360deg); } }

    /* ── Dropdown footer ─────────────────────────── */
    .dh-dropdown-footer {
      padding: 12px;
      border-top: 1.5px solid #fff0f3;
      text-align: center;
      background: linear-gradient(135deg,#fffafc,#fff0f3);
      flex-shrink: 0;
    }
    .dh-view-all {
      font-size: 13px;
      color: #ff0055;
      font-weight: 700;
      cursor: pointer;
    }
    .dh-view-all:hover { color: #c2185b; }

    /* ── Profile dropdown specifics ──────────────── */
    .dh-profile-dropdown { width: 250px; }
    .dh-profile-info {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .dh-profile-info-avatar {
      width: 38px; height: 38px;
      border-radius: 50%;
      border: 2.5px solid #ff0055;
      object-fit: cover;
      flex-shrink: 0;
    }
    .dh-profile-info-name {
      margin: 0;
      font-size: 13.5px;
      font-weight: 800;
      color: #1a0a10;
    }
    .dh-profile-info-id {
      margin: 2px 0 0;
      font-size: 11px;
      color: #ff4d88;
      font-weight: 600;
    }
    .dh-dropdown-menu-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 11px 18px;
      color: #555;
      text-decoration: none;
      font-size: 13.5px;
      font-weight: 600;
      transition: background .18s, color .18s;
      border: none;
      background: none;
      width: 100%;
      cursor: pointer;
      text-align: left;
      font-family: 'Poppins', sans-serif;
    }
    .dh-dropdown-menu-item:hover { background: #fff0f3; color: #ff0055; }
    .dh-dropdown-menu-item svg  { color: #ff4d88; flex-shrink: 0; }
    .dh-dropdown-menu-item:hover svg { color: #ff0055; }
    .dh-menu-divider {
      border: none;
      border-top: 1.5px solid #fff0f3;
      margin: 4px 0;
    }
    .dh-logout-item { color: #f44336 !important; }
    .dh-logout-item:hover { background: #fef2f2 !important; color: #d32f2f !important; }
    .dh-logout-item svg { color: #f44336 !important; }

    /* ── Mobile overlay backdrop for notification dropdown */
    .dh-notif-backdrop {
      display: none;
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,.35);
      z-index: 150;
      animation: fadeIn .2s ease;
    }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

    /* ── Mobile search bar (slide down) ──────────── */
    .dh-mobile-search-bar {
      position: fixed;
      top: 60px;
      left: 0; right: 0;
      background: linear-gradient(135deg,#9b003d,#e91e63);
      padding: 12px 14px;
      box-shadow: 0 8px 20px rgba(155,0,61,.2);
      z-index: 99;
      transform: translateY(0);
      animation: slideDown .25s ease;
    }
    @keyframes slideDown {
      from { transform: translateY(-100%); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }
    .dh-mobile-search-wrap { position: relative; }
    .dh-mobile-search-icon-inner {
      position: absolute;
      left: 13px; top: 50%;
      transform: translateY(-50%);
      color: rgba(255,255,255,.7);
      pointer-events: none;
      font-size: 13px;
    }
    .dh-mobile-search-input {
      width: 100%;
      padding: 11px 40px 11px 38px;
      border: 1.5px solid rgba(255,255,255,.3);
      border-radius: 12px;
      font-size: 14px;
      outline: none;
      background: rgba(255,255,255,.15);
      color: #fff;
      font-family: 'Poppins', sans-serif;
    }
    .dh-mobile-search-input::placeholder { color: rgba(255,255,255,.65); }
    .dh-mobile-search-input:focus {
      border-color: rgba(255,255,255,.7);
      background: rgba(255,255,255,.22);
    }
    .dh-mobile-search-close {
      position: absolute;
      right: 12px; top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: rgba(255,255,255,.8);
      cursor: pointer;
      display: flex;
      align-items: center;
      padding: 4px;
    }

    /* ── Logout modal ─────────────────────────────── */
    .dh-modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,.55);
      backdrop-filter: blur(6px);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      animation: fadeIn .25s ease;
    }
    .dh-modal {
      background: #fff;
      border-radius: 24px;
      width: 100%;
      max-width: 420px;
      padding: 32px 28px 28px;
      box-shadow: 0 24px 64px rgba(0,0,0,.25);
      text-align: center;
      animation: slideUp .3s ease;
      position: relative;
    }
    @keyframes slideUp {
      from { transform: translateY(28px) scale(.96); opacity: 0; }
      to   { transform: translateY(0)    scale(1);   opacity: 1; }
    }
    .dh-modal-close-btn {
      position: absolute;
      top: 14px; right: 14px;
      background: #f5f5f5;
      border: none;
      width: 32px; height: 32px;
      border-radius: 50%;
      font-size: 16px;
      color: #aaa;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background .2s, color .2s;
    }
    .dh-modal-close-btn:hover { background: #fef2f2; color: #f44336; }
    .dh-modal-icon {
      width: 72px; height: 72px;
      border-radius: 50%;
      background: linear-gradient(135deg,#fff0f3,#ffe4ec);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 16px;
      font-size: 32px;
      color: #f44336;
    }
    .dh-modal-title {
      font-size: 21px;
      font-weight: 800;
      color: #1a0a10;
      margin: 0 0 8px;
      font-family: 'Poppins', sans-serif;
    }
    .dh-modal-subtitle {
      font-size: 14px;
      color: #888;
      margin: 0 0 20px;
      line-height: 1.6;
      font-family: 'Poppins', sans-serif;
    }
    .dh-modal-user-chip {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      background: #fff5f7;
      border: 1.5px solid #ffe4ec;
      border-radius: 14px;
      padding: 10px 20px;
      margin-bottom: 22px;
    }
    .dh-modal-user-chip-name {
      font-size: 14px;
      font-weight: 700;
      color: #ff0055;
      font-family: 'Poppins', sans-serif;
    }
    .dh-modal-user-chip-id {
      font-size: 11.5px;
      color: #aaa;
      font-family: 'Poppins', sans-serif;
    }
    .dh-modal-actions {
      display: flex;
      gap: 12px;
      justify-content: center;
    }
    .dh-modal-btn {
      padding: 12px 28px;
      border: none;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      transition: transform .2s, box-shadow .2s;
      font-family: 'Poppins', sans-serif;
      display: flex;
      align-items: center;
      gap: 8px;
      justify-content: center;
      min-width: 120px;
    }
    .dh-modal-btn:hover  { transform: translateY(-2px); }
    .dh-modal-btn:active { transform: scale(.97); }
    .dh-modal-btn-cancel {
      background: #f5f5f5;
      color: #666;
    }
    .dh-modal-btn-cancel:hover { background: #eee; }
    .dh-modal-btn-logout {
      background: linear-gradient(135deg,#f44336,#d32f2f);
      color: #fff;
      box-shadow: 0 6px 18px rgba(244,67,54,.28);
    }
    .dh-modal-btn-logout:hover {
      box-shadow: 0 10px 28px rgba(244,67,54,.4);
    }

    /* ════════════════════════════════════════════
       RESPONSIVE BREAKPOINTS
    ════════════════════════════════════════════ */

    /* ── Hide/show helpers ── */
    .hide-mobile  { display: flex !important; }
    .show-mobile  { display: none  !important; }

    /* ── Tablet ≤ 900px ─────────────────────────── */
    @media (max-width: 900px) {
      .dh-search-input { width: 220px; }
    }

    /* ── ≤ 768px ────────────────────────────────── */
    @media (max-width: 768px) {
      .dh-search-desktop { display: none !important; }
      .hide-mobile  { display: none  !important; }
      .show-mobile  { display: flex  !important; }

      /* Notification dropdown — full-width sheet */
      .dh-notif-wrap > .dh-dropdown {
        position: fixed;
        top: auto;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        max-width: 100%;
        border-radius: 22px 22px 0 0;
        border-bottom: none;
        max-height: 80vh;
        display: flex;
        flex-direction: column;
        z-index: 201;
        animation: sheetUp .28s ease;
      }
      @keyframes sheetUp {
        from { transform: translateY(100%); }
        to   { transform: translateY(0); }
      }
      .dh-notif-backdrop { display: block; }
      .dh-dropdown-body { max-height: 55vh; }

      /* Profile dropdown — right-anchored */
      .dh-profile-wrap > .dh-dropdown {
        width: 240px;
        right: 0;
        position: absolute;
      }
    }

    /* ── ≤ 480px ────────────────────────────────── */
    @media (max-width: 480px) {
      .dh-header { padding: 0 12px; }

      .dh-profile-btn {
        padding: 4px 8px 4px 4px;
        gap: 6px;
      }
      .dh-profile-name  { display: none !important; }
      .dh-profile-id-pill { display: none !important; }
      .dh-chevron       { display: none !important; }
      .dh-avatar { width: 32px; height: 32px; }

      .dh-right { gap: 5px; }

      /* Notification dropdown */
      .dh-notif-wrap > .dh-dropdown { max-height: 85vh; }
      .dh-dropdown-body             { max-height: 60vh; }
      .dh-dropdown-header           { padding: 13px 14px; }
      .dh-dropdown-title            { font-size: 13.5px; }
      .dh-notif-item                { padding: 11px 14px; gap: 10px; }
      .dh-notif-emoji               { width: 34px; height: 34px; font-size: 17px; }
      .dh-notif-text                { font-size: 12.5px; }
      .dh-notif-time                { font-size: 10.5px; }

      /* Profile dropdown */
      .dh-profile-wrap > .dh-dropdown { width: calc(100vw - 16px); right: -6px; }

      /* Modal */
      .dh-modal { padding: 28px 20px 22px; border-radius: 20px; }
      .dh-modal-title { font-size: 19px; }
      .dh-modal-subtitle { font-size: 13px; }
      .dh-modal-btn { min-width: 100px; padding: 11px 18px; font-size: 13px; }
      .dh-modal-icon { width: 62px; height: 62px; font-size: 28px; }
    }

    /* ── ≤ 360px ────────────────────────────────── */
    @media (max-width: 360px) {
      .dh-header { padding: 0 10px; }
      .dh-right  { gap: 4px; }
      .dh-icon-btn { padding: 8px; }

      .dh-notif-item { padding: 10px 12px; gap: 8px; }
      .dh-notif-emoji { width: 30px; height: 30px; font-size: 15px; }
      .dh-notif-text { font-size: 12px; }
      .dh-notif-time { font-size: 10px; }

      .dh-profile-wrap > .dh-dropdown {
        width: calc(100vw - 12px);
        right: -4px;
      }
      .dh-dropdown-menu-item { padding: 10px 14px; font-size: 13px; }

      .dh-modal-actions  { flex-direction: column; }
      .dh-modal-btn      { width: 100%; min-width: unset; }
    }

    /* ── Landscape mobile (short height) ────────── */
    @media (max-height: 500px) and (orientation: landscape) {
      .dh-notif-wrap > .dh-dropdown { max-height: 90vh; }
      .dh-dropdown-body             { max-height: 65vh; }
    }
  `;

  return (
    <>
      <style>{styles}</style>

      {/* ────────────────────── HEADER ────────────────────── */}
      <header className="dh-header">

        {/* LEFT — hamburger */}
        <div className="dh-left">
          <button className="dh-icon-btn" onClick={toggleSidebar} aria-label="Toggle sidebar">
            <FaBars size={18} />
          </button>

        </div>

        {/* RIGHT — actions */}
        <div className="dh-right">

          {/* Mobile search toggle
          <button
            className="dh-icon-btn show-mobile"
            onClick={() => setShowSearch(v => !v)}
            aria-label="Search"
          >
            {showSearch ? <FaTimes size={17} /> : <FaSearch size={16} />}
          </button> */}

          {/* ── NOTIFICATIONS ── */}
          <div className="dh-notif-wrap" ref={notificationRef}>
            <button
              className="dh-icon-btn dh-notif-btn"
              aria-label="Notifications"
              onClick={() => {
                const next = !showNotifications;
                setShowNotifications(next);
                if (next) fetchNotifications();
              }}
            >
              <FaBell size={17} />
              {unreadCount > 0 && (
                <>
                  <span className="dh-notif-pulse" />
                  <span className="dh-notif-count">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                </>
              )}
            </button>

            {/* Mobile backdrop */}
            {showNotifications && (
              <div
                className="dh-notif-backdrop"
                onClick={() => setShowNotifications(false)}
              />
            )}

            {showNotifications && (
              <div className="dh-dropdown">
                {/* Header */}
                <div className="dh-dropdown-header">
                  <h4 className="dh-dropdown-title">
                    🔔 Notifications
                    {unreadCount > 0 && (
                      <span className="dh-new-badge">{unreadCount} new</span>
                    )}
                  </h4>
                  {unreadCount > 0 && (
                    <button
                      className="dh-mark-all"
                      onClick={() => handleMarkAsRead()}
                      disabled={notificationLoading}
                    >
                      Mark all read
                    </button>
                  )}
                </div>

                {/* Body */}
                <div className="dh-dropdown-body">
                  {notificationLoading ? (
                    <div className="dh-loading-notif">
                      <FaSpinner className="dh-spinner" size={22} color="#ff0055" />
                      <p style={{ marginTop: 10 }}>Loading notifications…</p>
                    </div>
                  ) : notifications.length > 0 ? (
                    notifications.map(item => (
                      <div
                        key={item.id}
                        className={`dh-notif-item${!item.is_read ? ' unread' : ''}`}
                        onClick={() => {
                          if (!item.is_read) handleMarkAsRead(item.id);
                          const link = getNotificationLink(item);
                          if (link && link !== '#') {
                            navigate(link);
                            setShowNotifications(false);
                          }
                        }}
                      >
                        <div className="dh-notif-emoji">
                          {item.icon || getNotificationIcon(item.notification_type)}
                        </div>
                        <div className="dh-notif-content">
                          <p className="dh-notif-text">
                            <strong>{item.title}</strong>
                            {item.message}
                          </p>
                          <p className="dh-notif-time">
                            {item.time_ago || 'Just now'}
                          </p>
                        </div>
                        <button
                          className="dh-notif-delete"
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteNotification(item.id);
                          }}
                          aria-label="Delete notification"
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="dh-empty-notif">
                      <span className="dh-empty-notif-icon">🔕</span>
                      No notifications yet
                    </div>
                  )}
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                  <div className="dh-dropdown-footer">
                    <span
                      className="dh-view-all"
                      onClick={() => {
                        navigate('/notifications');
                        setShowNotifications(false);
                      }}
                    >
                      View all notifications →
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── PROFILE ── */}
          <div className="dh-profile-wrap" ref={profileRef}>
            <button
              className="dh-profile-btn"
              onClick={() => setShowProfileMenu(v => !v)}
              aria-label="Profile menu"
            >
              <img src={avatarUrl} alt={displayName} className="dh-avatar" />
              <span className="dh-profile-name">{displayName}</span>
              <span className="dh-profile-id-pill">#{displayId}</span>
              <FaChevronDown className="dh-chevron" size={10} />
            </button>

            {showProfileMenu && (
              <div className="dh-dropdown dh-profile-dropdown">
                {/* Profile header */}
                <div className="dh-dropdown-header">
                  <div className="dh-profile-info">
                    <img
                      src={avatarUrl}
                      alt={displayName}
                      className="dh-profile-info-avatar"
                    />
                    <div>
                      <p className="dh-profile-info-name">{displayName}</p>
                      <p className="dh-profile-info-id">ID: #{displayId}</p>
                    </div>
                  </div>
                </div>

                {/* Use profileId for the profile link */}
               <a href="/profile" className="dh-dropdown-menu-item">
                  <FaUser size={14} /> My Profile
                </a>
                <a href="/shortlisted" className="dh-dropdown-menu-item">
                  <FaStar size={13} /> Shortlisted
                </a>
                <a href="/messages" className="dh-dropdown-menu-item">
                  <FaEnvelope size={13} /> Messages
                </a>
                <a href="/settings" className="dh-dropdown-menu-item">
                  <FaCog size={13} /> Settings
                </a>
                <hr className="dh-menu-divider" />
                <button
                  className="dh-dropdown-menu-item dh-logout-item"
                  onClick={handleLogoutClick}
                >
                  <FaSignOutAlt size={13} /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ────────────── MOBILE SEARCH BAR ──────────────
      {showSearch && (
        <div className="dh-mobile-search-bar">
          <div className="dh-mobile-search-wrap">
            <FaSearch className="dh-mobile-search-icon-inner" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by ID, Name, Location…"
              className="dh-mobile-search-input"
            />
            <button
              className="dh-mobile-search-close"
              onClick={() => setShowSearch(false)}
              aria-label="Close search"
            >
              <FaTimes size={16} />
            </button>
          </div>
        </div>
      )} */}

      {/* ────────────────── LOGOUT MODAL ─────────────── */}
      {showLogoutModal && (
        <div className="dh-modal-overlay" onClick={handleLogoutCancel}>
          <div
            className="dh-modal"
            onClick={e => e.stopPropagation()}
            ref={modalRef}
          >
            <button
              className="dh-modal-close-btn"
              onClick={handleLogoutCancel}
              aria-label="Close"
            >
              <FaTimes size={14} />
            </button>

            <div className="dh-modal-icon">
              <FaExclamationTriangle />
            </div>

            <h3 className="dh-modal-title">Logout?</h3>
            <p className="dh-modal-subtitle">
              You'll need to sign in again to access your account.
            </p>

            <div className="dh-modal-user-chip">
              <span className="dh-modal-user-chip-name">👤 {displayName}</span>
              <span className="dh-modal-user-chip-id">Profile ID: #{displayId}</span>
            </div>

            <div className="dh-modal-actions">
              <button
                className="dh-modal-btn dh-modal-btn-cancel"
                onClick={handleLogoutCancel}
              >
                <FaTimes size={13} /> Stay
              </button>
              <button
                className="dh-modal-btn dh-modal-btn-logout"
                onClick={handleLogoutConfirm}
              >
                <FaCheckCircle size={13} /> Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
